// https://www.streamlinehq.com/icons/streamline-light/clothes-footwear-accessories/clothes-design/clothes-design-tape-measure

import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { MetricNature } from '@upright/common/model/input/types';
import { Valence } from '@upright/common/model/types';
import { usePaiLensContext } from '@upright/ui/common/upright/core/impact-lenses/pais/common/use-pai-lens-context';
import styled, { css } from 'styled-components';
import { PlaceholderSVG } from '../../../impact-lens-profile/common/components/placeholder';
import { valenceToSide } from '../../../impact-lens-profile/common/utils';
import { useCarouselContext } from '../../../impact-lens-profile/context';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { getPaiNaturesForImpactCategory } from '../common/utils';
import { PaiBox } from './pai-box';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';

interface Props {
  impact: ImpactCategoryId;
  valence: Valence;
}

export function PaiBoxes(props: Props) {
  const { impact, valence } = props;
  // TODO: Better to use same context
  const ctx = usePaiLensContext();
  const pushGtmEvent = usePushGtmEvent();
  const baseContext = useCarouselContext<ImpactLensType.EU_PAIS>();
  const lens = baseContext.lens;

  const pais = ctx?.data.pais;
  if (!ctx || !pais || valence === Valence.POSITIVE) {
    return <></>;
  }
  const { selectionState, setSelectionState } = ctx;
  const isHighlighted = selectionState.impact === props.impact;
  const countByNature = getPaiNaturesForImpactCategory(impact, pais);

  const allPaiNatures = [
    MetricNature.UPRIGHT_ESTIMATE,
    MetricNature.UPRIGHT_MANUAL_ASSESSMENT,
    MetricNature.DIRECT_DISCLOSURE,
    MetricNature.THIRD_PARTY_ASSESSMENT,
  ];

  const filteredPaiNatures = allPaiNatures.filter(d => countByNature[d]);

  const isClickable = lens.isImpactDataAreaClickable?.(ctx.data, impact, valence) ?? false;
  const handlePaiBarClick = (() => {
    if (lens.onImpactDataAreaClick && isClickable) {
      return function () {
        pushGtmEvent({
          event: CustomGtmEvent.IMPACT_PROFILE_BAR_CLICK,
          data: {
            impactCode: impact,
            valence,
            lens: ImpactLensType.EU_PAIS,
          },
        });
        const newState = lens.onImpactDataAreaClick?.(selectionState, impact, valence);
        if (newState) {
          setSelectionState(newState);
        }
      };
    } else {
      return undefined;
    }
  })();

  const boxes = filteredPaiNatures.map((nature, index) => {
    const count = countByNature[nature];
    if (!count) {
      throw Error();
    }
    return (
      <Box
        key={nature}
        nature={nature}
        index={index}
        impact={impact}
        valence={valence}
        count={count}
        isHighlighted={isHighlighted}
      />
    );
  });

  return (
    <G onClick={handlePaiBarClick} isClickable={isClickable}>
      {boxes}
      <Clickable bounds={baseContext.layout.impactDataArea(valenceToSide(props.valence), props.impact)} />
    </G>
  );
}

const Clickable = styled(PlaceholderSVG)`
  opacity: 0;
  pointer-events: fill;
`;

const Box = styled(PaiBox)``;

const G = styled.g<{ isClickable: boolean }>`
  ${p =>
    p.isClickable
      ? css`
          cursor: pointer;
          &:hover ${Box} {
            opacity: 0.7;
          }
        `
      : ''}
`;
